import * as Yup from 'yup'
import {
  CollectingCompanyTableItem,
  DangerousDEEEEditItem,
  DangerousDEEEItem,
  NonDangerousDEEEEditItem,
  NonDangerousDEEEPayloadItem,
  WasteCategoryOption
} from 'components/waste-management/interfaces'
import { formatPayloadDate, formatUIDate } from 'utils/helpers'
import dayjs from 'dayjs'

export const getDailyReportName = report =>
  `${report.reportNo} - ${formatUIDate(report.creationDate)}`

export const getInitialValues = (
  editItem: DangerousDEEEEditItem | NonDangerousDEEEEditItem,
  hasDailyReports = true
) =>
  editItem
    ? {
        wasteType:
          editItem.wasteType ||
          editItem.dailyReports?.[0]?.wasteCategoryDailyReports?.[0]?.wasteReportCollectionType ||
          (editItem.wasteCategory?.wasteReportCollectionType === 'HAZARDOUS'
            ? 'HAZARDOUS'
            : 'NONHAZARDOUS'),
        wasteCategory: !hasDailyReports
          ? ((editItem as any).wasteCategoriesList || []).map((category: WasteCategoryOption) => ({
              label: category.name,
              value: category.id,
              wasteReportCollectionType: category.wasteReportCollectionType
            }))
          : editItem.wasteCategory
          ? {
              label: editItem.wasteCategory.name,
              value: editItem.wasteCategory.id,
              wasteReportCollectionType: editItem.wasteCategory.wasteReportCollectionType
            }
          : { label: '', value: '', wasteReportCollectionType: '' },
        collectingCompany: editItem.wasteCollectionCompany
          ? {
              label: editItem.wasteCollectionCompany.name,
              value: editItem.wasteCollectionCompany.id
            }
          : { label: '', value: '' },
        ...(hasDailyReports && {
          dailyReports: editItem.dailyReports.map(report => ({
            label: getDailyReportName(report),
            value: report.id
          }))
        }),
        ...(editItem.hasOwnProperty('wasteCategories') &&
          typeof (editItem as NonDangerousDEEEEditItem).wasteCategories === 'string' && {
            wasteCategories: (editItem as NonDangerousDEEEEditItem).wasteCategories
          }),
        wasteCode: editItem.wasteCode,
        operationTypes: editItem.operationTypes.map(type => ({
          label: type.description,
          value: type.name
        })),
        measuredWeight: +editItem.measuredWeight,
        ...(editItem.hasOwnProperty('expeditorEnvironmentAuthorizationNumber') && {
          expeditorEnvironmentAuthorizationNumber: (editItem as NonDangerousDEEEEditItem)
            .expeditorEnvironmentAuthorizationNumber
        }),
        ...(editItem.hasOwnProperty('expeditorEnvironmentAuthorizationExpirationDate') && {
          expeditorEnvironmentAuthorizationExpirationDate: (editItem as NonDangerousDEEEEditItem)
            .expeditorEnvironmentAuthorizationExpirationDate
        }),
        carrierId: editItem.carrier.id,
        carrierName: editItem.carrier.name,
        carrierTradeRegisterNumber: editItem.carrier.tradeRegisterNumber,
        cif: editItem.carrier.cif,
        headquarter: editItem.carrier.headquarter,
        delegateId: editItem.delegate.id,
        delegateName: editItem.delegate.name,
        identityCardSeries: editItem.delegate.identityCardSeries,
        identityCardNumber: editItem.delegate.identityCardNumber,
        cnp: editItem.delegate.cnp,
        vehicleNumber: editItem.delegate.vehicleNumber,
        transportLicenseNumber: editItem.delegate.transportLicenseNumber,
        transportLicenseExpirationDate: editItem.delegate.transportLicenseExpirationDate,
        ...(editItem.hasOwnProperty('carrierHandoverDate') && {
          carrierHandoverDate: formatPayloadDate(
            (editItem as NonDangerousDEEEEditItem).carrierHandoverDate
          )
        }),
        ...((editItem.hasOwnProperty('environmentalAuthorizationNo') || !hasDailyReports) && {
          environmentalAuthorizationNo: !hasDailyReports
            ? editItem.wasteCollectionCompany?.environmentalPermitNumber
            : (editItem as NonDangerousDEEEEditItem).environmentalAuthorizationNo
        }),
        ...((editItem.hasOwnProperty('environmentalPermitExpirationDate') || !hasDailyReports) && {
          environmentalPermitExpirationDate: !hasDailyReports
            ? editItem.wasteCollectionCompany?.environmentalPermitExpirationDate
            : (editItem as NonDangerousDEEEEditItem).environmentalPermitExpirationDate
        }),
        ...(editItem.hasOwnProperty('wasteCollectionCompanyReceivedDate') && {
          wasteCollectionCompanyReceivedDate: formatPayloadDate(
            (editItem as DangerousDEEEEditItem).wasteCollectionCompanyReceivedDate
          )
        }),
        ...(editItem.hasOwnProperty('hazardousWasteName') && {
          hazardousWasteName: (editItem as DangerousDEEEEditItem).hazardousWasteName
        }),
        ...(editItem.hasOwnProperty('wasteWeightPerYear') && {
          wasteWeightPerYear:
            (editItem as DangerousDEEEEditItem).wasteWeightPerYear === true ? 'true' : 'false'
        }),
        ...(editItem.hasOwnProperty('transportApprovalFormNumber') && {
          transportApprovalFormNumber: (editItem as DangerousDEEEEditItem)
            .transportApprovalFormNumber
        }),
        ...(editItem.hasOwnProperty('carrierReceivedWeight') && {
          carrierReceivedWeight: (editItem as DangerousDEEEEditItem).carrierReceivedWeight
        }),
        ...(editItem.hasOwnProperty('wasteCollectionCompanyReceivedWeight') && {
          wasteCollectionCompanyReceivedWeight: (editItem as DangerousDEEEEditItem)
            .wasteCollectionCompanyReceivedWeight
        }),
        ...(editItem.hasOwnProperty('wasteCollectionCompanyRejectedWeight') && {
          wasteCollectionCompanyRejectedWeight: (editItem as DangerousDEEEEditItem)
            .wasteCollectionCompanyRejectedWeight
        }),
        ...(editItem.hasOwnProperty('downloadDate') && {
          downloadDate: (editItem as NonDangerousDEEEEditItem).downloadDate
            ? formatPayloadDate((editItem as NonDangerousDEEEEditItem).downloadDate)
            : ''
        }),
        ...(editItem.hasOwnProperty('wasteCollectionCompany') &&
          editItem.wasteCollectionCompany && {
            companyId: editItem.wasteCollectionCompany.id,
            companyName: editItem.wasteCollectionCompany.name,
            wasteCompanyName: editItem.wasteCollectionCompany.name,
            companyHeadquarter: editItem.wasteCollectionCompany.headquarter,
            companyCounty: editItem.wasteCollectionCompany.county,
            companyCif: editItem.wasteCollectionCompany.cif,
            companyTradeRegisterNumber: editItem.wasteCollectionCompany.tradeRegisterNumber
          })
      }
    : {
        wasteType: 'NONHAZARDOUS',
        wasteCategory: hasDailyReports
          ? { label: '', value: '', wasteReportCollectionType: '' }
          : [],
        dailyReports: [],
        wasteCategories: '',
        wasteCode: '',
        operationTypes: [],
        measuredWeight: '',
        expeditorEnvironmentAuthorizationNumber: '',
        expeditorEnvironmentAuthorizationExpirationDate: '',
        carrierName: '',
        carrierTradeRegisterNumber: '',
        cif: '',
        headquarter: '',
        delegateName: '',
        identityCardSeries: '',
        identityCardNumber: '',
        cnp: '',
        vehicleNumber: '',
        transportLicenseNumber: '',
        transportLicenseExpirationDate: '',
        carrierHandoverDate: dayjs(),
        environmentalAuthorizationNo: '',
        environmentalPermitExpirationDate: '',
        wasteCollectionCompanyReceivedDate: '',
        downloadDate: null,
        name: '',
        wasteWeightPerYear: 'false',
        transportApprovalFormNumber: '',
        carrierReceivedWeight: null,
        wasteCollectionCompanyReceivedWeight: '',
        wasteCollectionCompanyRejectedWeight: '',
        collectingCompany: { label: '', value: '' },
        companyId: '',
        companyName: '',
        wasteCompanyName: '',
        companyHeadquarter: '',
        companyCounty: '',
        companyCif: '',
        companyTradeRegisterNumber: ''
      }

const getNonDangerousDEEEValidationSchema = isExternalArticlesReport =>
  Yup.object({
    ...(isExternalArticlesReport && {
      wasteCategory: Yup.array().min(1, 'Acest camp este obligatoriu.')
    }),
    ...(!isExternalArticlesReport && {
      collectingCompany: Yup.object().shape({
        label: Yup.string().required('Acest camp este obligatoriu.'),
        value: Yup.string().required('Acest camp este obligatoriu.')
      })
    }),
    wasteCategories: Yup.string(),
    wasteCode: Yup.string().required('Acest camp este obligatoriu.'),
    operationTypes: Yup.array().min(1, 'Acest camp este obligatoriu.'),
    measuredWeight: Yup.number().required('Acest camp este obligatoriu.'),
    expeditorEnvironmentAuthorizationNumber: Yup.string().nullable(),
    expeditorEnvironmentAuthorizationExpirationDate: Yup.string().nullable(),
    carrierName: Yup.string().required('Acest camp este obligatoriu.'),
    cif: Yup.string().required('Acest camp este obligatoriu.'),
    headquarter: Yup.string().required('Acest camp este obligatoriu.'),
    delegateName: Yup.string().required('Acest camp este obligatoriu.'),
    identityCardSeries: Yup.string().nullable(),
    identityCardNumber: Yup.string().nullable(),
    cnp: Yup.string().nullable(),
    vehicleNumber: Yup.string().required('Acest camp este obligatoriu.'),
    transportLicenseNumber: Yup.string().required('Acest camp este obligatoriu.'),
    transportLicenseExpirationDate: Yup.string().required('Acest camp este obligatoriu.'),
    environmentalAuthorizationNo: Yup.string().nullable(),
    environmentalPermitExpirationDate: Yup.string().nullable(),
    downloadDate: Yup.date().nullable(),
    ...(isExternalArticlesReport
      ? {
          companyName: Yup.string().required('Acest camp este obligatoriu.'),
          companyHeadquarter: Yup.string().required('Acest camp este obligatoriu.'),
          companyCounty: Yup.string().required('Acest camp este obligatoriu.'),
          companyCif: Yup.string().required('Acest camp este obligatoriu.'),
          companyTradeRegisterNumber: Yup.string().nullable()
        }
      : {
          wasteCompanyName: Yup.string().required('Acest camp este obligatoriu.')
        })
  })

const getDangerousDEEEValidationSchema = isExternalArticlesReport =>
  Yup.object({
    ...(isExternalArticlesReport && {
      wasteCategory: Yup.array().min(1, 'Acest camp este obligatoriu.')
    }),
    ...(!isExternalArticlesReport && {
      collectingCompany: Yup.object().shape({
        label: Yup.string().required('Acest camp este obligatoriu.'),
        value: Yup.string().required('Acest camp este obligatoriu.')
      })
    }),
    hazardousWasteName: Yup.string().required('Acest camp este obligatoriu.'),
    wasteCode: Yup.string().required('Acest camp este obligatoriu.'),
    wasteWeightPerYear: Yup.string().required('Acest camp este obligatoriu.'),
    transportApprovalFormNumber: Yup.string().when(
      'wasteWeightPerYear',
      ([wasteWeightPerYear], schema) =>
        wasteWeightPerYear === 'false'
          ? schema.nullable()
          : schema.required('Acest camp este obligatoriu.')
    ),
    measuredWeight: Yup.number().required('Acest camp este obligatoriu.'),
    carrierName: Yup.string().required('Acest camp este obligatoriu.'),
    // carrierTradeRegisterNumber: Yup.string().required('Acest camp este obligatoriu.'),
    cif: Yup.string().required('Acest camp este obligatoriu.'),
    headquarter: Yup.string().required('Acest camp este obligatoriu.'),
    delegateName: Yup.string().required('Acest camp este obligatoriu.'),
    vehicleNumber: Yup.string().required('Acest camp este obligatoriu.'),
    carrierReceivedWeight: Yup.number().required('Acest camp este obligatoriu.'),
    operationTypes: Yup.array().min(1, 'Acest camp este obligatoriu.'),
    wasteCollectionCompanyReceivedWeight: Yup.number().nullable(),
    wasteCollectionCompanyRejectedWeight: Yup.number().nullable(),
    downloadDate: Yup.date().nullable(),
    ...(isExternalArticlesReport
      ? {
          companyName: Yup.string().required('Acest camp este obligatoriu.'),
          companyHeadquarter: Yup.string().nullable(),
          companyCounty: Yup.string().nullable(),
          companyCif: Yup.string().required('Acest camp este obligatoriu.'),
          companyTradeRegisterNumber: Yup.string().nullable()
        }
      : {
          wasteCompanyName: Yup.string().required('Acest camp este obligatoriu.')
        })
  })

export const getValidationSchema = (isDangerous: boolean, isExternalArticlesReport?: boolean) =>
  isDangerous
    ? getDangerousDEEEValidationSchema(isExternalArticlesReport)
    : getNonDangerousDEEEValidationSchema(isExternalArticlesReport)

export const isDangerousDEEEItem = (item: any): item is DangerousDEEEItem =>
  item &&
  (typeof item.wasteWeightPerYear === 'boolean' || typeof item.wasteWeightPerYear === 'string') &&
  typeof item.carrierReceivedWeight === 'number'

export const isNonDangerousDEEEItem = (item: any): item is NonDangerousDEEEPayloadItem =>
  item && typeof item.carrierReceivedWeight === 'object'

export const getCurrentCompanyDescription = (company: CollectingCompanyTableItem) =>
  `${company.name}, ${company.headquarter}, CIF ${company.cif}`

export const getNonNegativeNumberInputHandlers = (
  setFieldValue: (field: string, value: string | number) => void,
  fieldName: string
) => ({
  onChange: (event: { currentTarget: { value: string } }) => {
    const value = event.currentTarget.value
    if (value === '') {
      setFieldValue(fieldName, '')
      return
    }
    const num = Number(value)
    if (Number.isNaN(num)) {
      return
    }
    setFieldValue(fieldName, num < 0 ? 0 : value)
  },
  onKeyDown: (event: { key: string; preventDefault: () => void }) => {
    if (event.key === '-' || event.key === 'e' || event.key === 'E' || event.key === '+') {
      event.preventDefault()
    }
  }
})
